import { useAppDispatch, useAppSelector } from '@/store/hooks';
import {
  lastNDays,
  selectReportingV2Filters,
  setReportingV2Filters,
} from '@/store/slices/reportingV2/reportingV2Slice';
import { CalendarTodayOutlined, KeyboardArrowDownRounded } from '@mui/icons-material';
import { Box, Button, Divider, Menu, MenuItem, Popover, Stack, TextField } from '@mui/material';
import { useState } from 'react';
import { COLORS } from '../theme';

const pad = (n: number) => String(n).padStart(2, '0');
const ymd = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
/** 'YYYY-MM-DD' → 'MM/DD/YYYY' for the pill's custom-range label. */
const mdy = (s: string) => {
  const [y, m, d] = s.split('-');
  return y && m && d ? `${m}/${d}/${y}` : s;
};

/**
 * The presets that mean "relative to today", by label. Custom Dashboards persists
 * an ABSOLUTE range plus its preset label and re-anchors on open only when the
 * label is one of these — so a "Last Month" or custom window stays pinned even
 * when its span happens to be 30 days. Kept here so the two bars can't drift.
 */
export const RELATIVE_PRESET_DAYS: Record<string, number> = {
  'Last 7 Days': 7,
  'Last 14 Days': 14,
  'Last 30 Days': 30,
  'Last 90 Days': 90,
};

export interface DateRangeValue {
  dateStart: string;
  dateEnd: string;
  datePreset: string;
}

/**
 * Why the custom-range Apply button is disabled, if it is (#4572 — it used to
 * just grey out with nothing telling the user why). Empty fields say nothing:
 * a field left blank while the user is still typing isn't an error yet.
 */
export const customRangeError = (draftStart: string, draftEnd: string): string => {
  if (!draftStart || !draftEnd) return '';
  return draftStart > draftEnd ? 'Start date must be on or before the end date.' : '';
};

const PRESETS: { label: string; range: () => { start: string; end: string } }[] = [
  { label: 'Last 7 Days', range: () => lastNDays(7) },
  { label: 'Last 14 Days', range: () => lastNDays(14) },
  { label: 'Last 30 Days', range: () => lastNDays(30) },
  { label: 'Last 90 Days', range: () => lastNDays(90) },
  {
    label: 'This Month',
    range: () => {
      const now = new Date();
      return { start: ymd(new Date(now.getFullYear(), now.getMonth(), 1)), end: ymd(now) };
    },
  },
  {
    label: 'Last Month',
    range: () => {
      const now = new Date();
      const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const end = new Date(now.getFullYear(), now.getMonth(), 0); // day 0 = last day of prev month
      return { start: ymd(start), end: ymd(end) };
    },
  },
];

/**
 * Date control matching the FLANK 2.0 pill. Renders as a non-button (role
 * generic) on purpose — a real button labelled "Last 7 Days" would match the
 * "Day" granularity button under getByRole's substring matching. Opens a preset
 * menu; "Custom range…" reveals a start/end popover. Changing the range updates
 * filter state, which refetches every widget through the batched endpoint.
 */
const DateRangeControl = ({
  value,
  onChange,
}: {
  value?: DateRangeValue;
  onChange?: (next: DateRangeValue) => void;
} = {}) => {
  const dispatch = useAppDispatch();
  const storeFilters = useAppSelector(selectReportingV2Filters);
  // Uncontrolled (Reporting) reads and writes the shared reportingV2 slice.
  // Controlled (Custom Dashboards) owns filters per dashboard, so it passes its
  // own value in and persists the result itself.
  const filters = value ?? storeFilters;
  const commit = (next: DateRangeValue) => {
    if (onChange) onChange(next);
    else dispatch(setReportingV2Filters(next));
  };
  const [menuEl, setMenuEl] = useState<HTMLElement | null>(null);
  const [customEl, setCustomEl] = useState<HTMLElement | null>(null);
  const [draftStart, setDraftStart] = useState(filters.dateStart);
  const [draftEnd, setDraftEnd] = useState(filters.dateEnd);

  const applyPreset = (label: string, range: () => { start: string; end: string }) => {
    const { start, end } = range();
    commit({ dateStart: start, dateEnd: end, datePreset: label });
    setMenuEl(null);
  };

  const openCustom = () => {
    setDraftStart(filters.dateStart);
    setDraftEnd(filters.dateEnd);
    // Anchor to the date pill, NOT the clicked MenuItem: the menu closes on the
    // same click and unmounts the item, which would leave the Popover with a
    // detached anchor and snap it to the top-left of the screen.
    setCustomEl(menuEl);
    setMenuEl(null);
  };

  const customValid = Boolean(draftStart && draftEnd && draftStart <= draftEnd);
  const customError = customRangeError(draftStart, draftEnd);
  const applyCustom = () => {
    if (!customValid) return;
    commit({ dateStart: draftStart, dateEnd: draftEnd, datePreset: 'Custom' });
    setCustomEl(null);
  };

  // A custom range shows the actual dates on the pill instead of the generic
  // "Custom" label, so the applied window is visible at a glance (#4127 Task 3).
  const pillLabel =
    filters.datePreset === 'Custom' ? `${mdy(filters.dateStart)} – ${mdy(filters.dateEnd)}` : filters.datePreset;

  return (
    <>
      <Box
        // Kept as a role="button" Box (not <Button>) on purpose — see component
        // doc above — but made keyboard-focusable and operable with Enter/Space
        // so it isn't a mouse-only control.
        role='button'
        tabIndex={0}
        aria-haspopup='true'
        aria-expanded={Boolean(menuEl)}
        onClick={event => setMenuEl(event.currentTarget)}
        onKeyDown={event => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            setMenuEl(event.currentTarget);
          }
        }}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.75,
          px: 1.75,
          py: 1.1,
          borderRadius: '10px',
          border: `1px solid ${menuEl ? COLORS.primaryBorder : COLORS.border}`,
          bgcolor: '#fff',
          color: COLORS.text,
          fontSize: '0.875rem',
          fontWeight: 500,
          cursor: 'pointer',
          userSelect: 'none',
        }}
      >
        <CalendarTodayOutlined sx={{ fontSize: 16, color: COLORS.muted }} />
        {pillLabel}
        <KeyboardArrowDownRounded sx={{ fontSize: 18, color: COLORS.muted }} />
      </Box>

      <Menu
        anchorEl={menuEl}
        open={Boolean(menuEl)}
        onClose={() => setMenuEl(null)}
        slotProps={{ paper: { sx: { mt: 0.5, minWidth: 180, borderRadius: '10px' } } }}
      >
        {PRESETS.map(preset => (
          <MenuItem
            key={preset.label}
            dense
            selected={preset.label === filters.datePreset}
            onClick={() => applyPreset(preset.label, preset.range)}
          >
            {preset.label}
          </MenuItem>
        ))}
        <Divider />
        <MenuItem dense selected={filters.datePreset === 'Custom'} onClick={openCustom}>
          Custom range…
        </MenuItem>
      </Menu>

      <Popover
        anchorEl={customEl}
        open={Boolean(customEl)}
        onClose={() => setCustomEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{ paper: { sx: { mt: 0.5, borderRadius: '10px' } } }}
      >
        <Stack sx={{ p: 2, gap: 1.5, width: 240 }}>
          <TextField
            type='date'
            label='Start'
            size='small'
            value={draftStart}
            onChange={event => setDraftStart(event.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            type='date'
            label='End'
            size='small'
            value={draftEnd}
            onChange={event => setDraftEnd(event.target.value)}
            InputLabelProps={{ shrink: true }}
            error={Boolean(customError)}
            helperText={customError || ' '}
          />
          <Button
            variant='contained'
            disableElevation
            disabled={!customValid}
            onClick={applyCustom}
            sx={{ textTransform: 'none', bgcolor: COLORS.primary, '&:hover': { bgcolor: COLORS.primaryHover } }}
          >
            Apply range
          </Button>
        </Stack>
      </Popover>
    </>
  );
};

export default DateRangeControl;
